import type { UserAttributes } from '@lib/analytics/AnalyticsService'
import {
    ATTRIBUTION_PARAM_KEYS,
    TRACK_ONCE_KEYS,
} from '@lib/analytics/constants'
import { hasCookieConsent } from '@lib/hooks/useCookieConsent'
import mixpanel from 'mixpanel-browser'

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN
type EventProperties = Record<string, any>

export const init = (searchParams?: URLSearchParams) => {
    if (!MIXPANEL_TOKEN) {
        console.warn('Mixpanel token is missing! Check your .env file.')
        return
    }

    mixpanel.init(MIXPANEL_TOKEN, {
        autocapture: false,
        record_sessions_percent: 10, // only record 10% of sessions
        record_idle_timeout_ms: 300000, // stop recording after 5 minutes idle
        record_block_class: 'mp-sensitive', // block elements with this class
        record_mask_text_selector: '.mp-mask', // mask text in elements with this class
        record_max_ms: 1800000, // max 30 minutes of recording per session
        record_heatmap_data: true, // enable heatmap click tracking during recorded sessions
    } as any)

    // Register attribution parameters if provided
    if (searchParams) {
        registerAttributionParameters(searchParams)
    }
}

const registerAttributionParameters = (searchParams: URLSearchParams) => {
    try {
        const attributionParams: Record<string, string> = {}

        ATTRIBUTION_PARAM_KEYS.forEach((param) => {
            const value = searchParams.get(param)
            if (value) {
                attributionParams[param] = value
            }
        })

        // Only register if we have attribution parameters
        // Use register to always capture the latest attribution
        if (Object.keys(attributionParams).length > 0) {
            mixpanel.register(attributionParams)
        }
    } catch (error) {
        console.warn('Failed to register attribution parameters:', error)
    }
}

export const identifyUser = (userId: string) => {
    try {
        if (!hasCookieConsent()) {
            return
        }

        mixpanel.identify(userId)
        mixpanel.people.set({
            $browser: navigator.userAgent,
        })
    } catch (error) {
        console.warn('Failed to identify Mixpanel user:', error)
    }
}

export const updateAttributes = (userAttributes: UserAttributes) => {
    if (!hasCookieConsent()) {
        return
    }

    // Attributes that should only be set once (rarely change)
    const onceOnlyAttributes: Partial<UserAttributes> = {}
    // Attributes that can be updated (may change over time)
    const updatableAttributes: Partial<UserAttributes> = {}

    // Split attributes into once-only vs updatable
    Object.entries(userAttributes).forEach(([key, value]) => {
        if (TRACK_ONCE_KEYS.includes(key)) {
            onceOnlyAttributes[key as keyof UserAttributes] = value
        } else {
            updatableAttributes[key as keyof UserAttributes] = value
        }
    })

    // Register attributes that should only be set once
    if (Object.keys(onceOnlyAttributes).length > 0) {
        mixpanel.register_once(onceOnlyAttributes)
    }

    // Register attributes that can be updated
    if (Object.keys(updatableAttributes).length > 0) {
        mixpanel.register(updatableAttributes)
    }
}

export const track = (eventName: string, properties: EventProperties = {}) => {
    try {
        if (!eventName) {
            console.warn('Event name is required for tracking')
            return
        }

        if (!hasCookieConsent()) {
            return
        }

        const baseProperties = {
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
        }

        mixpanel.track(eventName, {
            ...baseProperties,
            ...properties,
        })
    } catch (error) {
        console.warn('Failed to track event:', eventName, error)
    }
}

export const startSessionRecording = () => {
    try {
        if (!hasCookieConsent()) {
            return
        }

        mixpanel.start_session_recording()
    } catch (error) {
        console.warn('Failed to start session recording:', error)
    }
}

export const stopSessionRecording = () => {
    try {
        mixpanel.stop_session_recording()
    } catch (error) {
        console.warn('Failed to stop session recording:', error)
    }
}
